import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import { usePlannerStore } from '@/views/3d-planner/store/plannerStore';
import { usePlannerSceneViewModel } from '@/views/3d-planner/store/plannerSceneViewModel';
import type { PlannerMode } from '@/views/3d-planner/store/plannerState';
import { useTemplateSyncStore } from '@/views/3d-planner/template/templateSyncStore';
import { templatesAreEqual } from '@/views/3d-planner/template/templateComparison';
import { syncTemplate } from '@/views/3d-planner/template/manualTemplateState';
import type { PlanApprovalState } from '@/domain/plan/planApprovalState';

/**
 * What the caller is about to do to this plan.
 *
 * Approving is the clinical act and only a surgeon or an administrator may do it. Proposing is the
 * clerical half - preparing the plan and its report for a surgeon to review - and exists only for
 * callers who cannot approve. Nobody chooses between them; the act follows from what the caller
 * may do, and the API decides again on the request.
 */
export type PlanAct = 'approve' | 'propose' | 'none';

const LABELS: Record<PlanAct, string> = {
    approve: 'Approve & Generate Plan',
    propose: 'Propose Plan',
    none: '',
};

/**
 * What a waiting proposal means to the caller looking at it.
 *
 * A preparer sees a disabled button here - they proposed the plan and cannot approve it - so
 * without this they are given no reason for it.
 */
const PROPOSAL_HINTS: Record<PlanAct, string | null> = {
    approve: 'This plan was proposed and is waiting for your approval.',
    propose: 'This plan was proposed and is waiting for the surgeon to approve it.',
    none: null,
};

type PlannerStore = ReturnType<typeof usePlannerStore>;
type SyncStore = ReturnType<typeof useTemplateSyncStore>;

const ACTS: Record<PlanApprovalState, PlanAct> = {
    approved: 'approve',
    proposed: 'propose',
};

/**
 * What this caller is offered, as the server named it.
 *
 * Not derived from role names. Approving takes being the surgeon of *this* case (or an admin who
 * reaches it), which a role name cannot answer: a surgeon who is also a linked sales rep for
 * somebody else's case may only propose there. Reading the act off the case is what keeps the
 * button's label and the state the server will write from disagreeing.
 */
function actFor(store: PlannerStore): PlanAct {
    const state = store.case?.callerPlanApprovalState;

    return state ? ACTS[state] : 'none';
}

/**
 * Whether the case differs from the last plan made of it.
 *
 * When a plan exists this is a URL comparison. When none does, the API cannot tell us whether the
 * case is dirty, so the closest available answer is whether the manual template still matches the
 * automated one it started as.
 */
function changedSinceLastPlan(store: PlannerStore): boolean {
    if (store.case === null) {
        return false;
    }

    if (store.case.manualPlanTemplateUrl !== null) {
        return store.case.manualPlanTemplateUrl !== store.case.manualTemplateCanonicalUrl;
    }

    if (!store.template) {
        return false;
    }

    return !templatesAreEqual(syncTemplate(store.template), store.case.autoTemplate);
}

/** Changes made in this session, which the loaded state cannot know about. */
function trackChangesMadeHere(store: PlannerStore, sync: SyncStore): Ref<boolean> {
    const changed = ref(false);

    watch(
        () => sync.hasUpdate,
        (newValue, oldValue) => {
            if (oldValue && !newValue) {
                changed.value = true;
            }
        },
    );

    watch(
        () => store.currentOperation,
        (newValue, oldValue) => {
            if (oldValue === 'approve-plan' && newValue === null) {
                changed.value = false;
            }
        },
    );

    return changed;
}

type ButtonSituation = {
    act: PlanAct;
    plannerMode: PlannerMode;
    valid: boolean;

    /** An edit is still on its way to the server, so there is nothing settled to plan from. */
    unsavedUpdate: boolean;

    changed: boolean;
    proposalWaiting: boolean;
};

function isButtonEnabled(situation: ButtonSituation): boolean {
    const { act, plannerMode, valid, unsavedUpdate, changed, proposalWaiting } = situation;

    if (plannerMode === 'disabled' || act === 'none' || !valid || unsavedUpdate) {
        return false;
    }

    // A surgeon may approve a proposal that nothing has changed since - that is the whole point of
    // it having been proposed.
    return changed || (proposalWaiting && act === 'approve');
}

/**
 * The view model behind the approve/propose button.
 *
 * `ApprovePlan.vue` sits under `src/views/**`, where lint-staged escalates the thin-view rules to
 * commit-blocking errors, so this logic cannot live in the component.
 */
export function useApprovePlanViewModel(valid: () => boolean) {
    const store = usePlannerStore();
    const scene = usePlannerSceneViewModel();
    const sync = useTemplateSyncStore();

    const changedHere = trackChangesMadeHere(store, sync);
    const act: ComputedRef<PlanAct> = computed(() => actFor(store));
    const changesNotPlannedYet = computed(() => changedHere.value || changedSinceLastPlan(store));

    /** A proposed plan is still waiting on a surgeon, so approving it remains available. */
    const proposalAwaitingApproval = computed(
        () => store.case?.manualPlanApprovalState === 'proposed',
    );

    return {
        act,
        label: computed(() => LABELS[act.value]),
        changesNotPlannedYet,
        proposalAwaitingApproval,
        proposalHint: computed(() =>
            proposalAwaitingApproval.value ? PROPOSAL_HINTS[act.value] : null,
        ),
        isSaving: computed(() => sync.isSaving),
        buttonDisabled: computed(
            () =>
                !isButtonEnabled({
                    act: act.value,
                    plannerMode: scene.plannerMode,
                    valid: valid(),
                    unsavedUpdate: sync.hasUpdate,
                    changed: changesNotPlannedYet.value,
                    proposalWaiting: proposalAwaitingApproval.value,
                }),
        ),
        submit: () => store.approvePlan(),
    };
}
